/** @type {Map<string, number>} */
const selectionAnchors = new Map();

/**
 * @param {string | null | undefined} groupId
 * @param {number} [fallback]
 * @returns {number | undefined}
 */
export function getSelectionAnchor(groupId, fallback) {
    if (groupId != null && selectionAnchors.has(groupId)) {
        return selectionAnchors.get(groupId);
    }
    return fallback;
}

/**
 * @param {string | null | undefined} groupId
 * @param {number} [index]
 */
export function setSelectionAnchor(groupId, index) {
    if (groupId == null) return;
    if (!Number.isInteger(index)) {
        selectionAnchors.delete(groupId);
        return;
    }
    selectionAnchors.set(groupId, index);
}

/** Clears stored origins. Exported for tests. */
export function clearSelectionAnchors() {
    selectionAnchors.clear();
}

/**
 * 1-based event IDs covering the current selection.
 *
 * Multiple selected events use numeric min/max (not string sort).
 * A single selected event ripples through the rest of the group unless
 * `rippleIfSingle` is false.
 *
 * @param {number[]} selected 0-based indexes
 * @param {number} eventCount
 * @param {{ rippleIfSingle?: boolean }} [options]
 * @returns {{ start: number, end: number }}
 */
export function getSelectedEventIdRange(selected, eventCount, options = {}) {
    const rippleIfSingle = options.rippleIfSingle !== false;
    if (!Array.isArray(selected) || selected.length === 0) {
        return { start: 1, end: rippleIfSingle ? eventCount : 1 };
    }
    let min = selected[0];
    let max = selected[0];
    for (const idx of selected) {
        if (idx < min) min = idx;
        if (idx > max) max = idx;
    }
    return {
        start: min + 1,
        end: rippleIfSingle && selected.length === 1 ? eventCount : max + 1,
    };
}

/**
 * @param {number} lo
 * @param {number} hi
 * @returns {number[]}
 */
function contiguousRange(lo, hi) {
    const range = [];
    for (let i = lo; i <= hi; i += 1) range.push(i);
    return range;
}

/**
 * Apply a click to the current event selection.
 *
 *   - Ctrl/Cmd: toggle the clicked event; origin moves to that event
 *   - Shift: select the range from the stable origin to the clicked event
 *   - no modifier: single-select the clicked event and set it as the origin
 *
 * `selected` is always written in ascending index order so `selected[0]` stays
 * the chronological first event used by preview/editor.
 *
 * @param {object} input
 * @param {number} input.eventIndex
 * @param {{ ctrlKey?: boolean, metaKey?: boolean, shiftKey?: boolean }} [input.modifiers]
 * @param {number[]} [input.currentSelected]
 * @param {number[]} [input.filteredIndices] If omitted, a shift range includes every index in [lo, hi].
 * @param {number} [input.anchor]
 * @returns {{ selected: number[], anchor: number }}
 */
export function applyEventClick({
    eventIndex,
    modifiers,
    currentSelected,
    filteredIndices,
    anchor,
}) {
    const current = Array.isArray(currentSelected) ? currentSelected : [];
    const ctrlOrMeta = Boolean(modifiers?.ctrlKey || modifiers?.metaKey);
    const shift = Boolean(modifiers?.shiftKey);
    const resolvedAnchor = Number.isInteger(anchor) ? anchor : current[0];

    if (ctrlOrMeta) {
        const selected = current.includes(eventIndex)
            ? current.filter((idx) => idx !== eventIndex)
            : [...current, eventIndex];
        return { selected, anchor: eventIndex };
    }

    if (shift && current.length > 0 && Number.isInteger(resolvedAnchor)) {
        const lo = Math.min(resolvedAnchor, eventIndex);
        const hi = Math.max(resolvedAnchor, eventIndex);
        const selected = Array.isArray(filteredIndices)
            ? filteredIndices.filter((i) => i >= lo && i <= hi)
            : contiguousRange(lo, hi);
        return { selected, anchor: resolvedAnchor };
    }

    return { selected: [eventIndex], anchor: eventIndex };
}
